import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Clock, User, Building, Activity, Search } from 'lucide-react';
import { Database } from '@/integrations/supabase/types';


type SupportSession = Database['public']['Tables']['support_sessions']['Row'] & {
  shops?: {
    id: string;
    name: string;
  };
  profiles?: {
    id: string;
    email: string | null;
  };
};

export default function SupportDashboard() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<SupportSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'expired'>('active');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadSessions();
  }, [filter]);

  const loadSessions = async () => {
    setLoading(true);
    let query = supabase
      .from('support_sessions')
      .select(`
        *,
        shops:target_shop_id (
          id,
          name
        ),
        profiles:started_by_user_id (
          id,
          email
        )
      `)
      .order('created_at', { ascending: false })
      .limit(100);

    if (filter === 'active') {
      query = query.eq('active', true).gt('expires_at', new Date().toISOString());
    } else if (filter === 'expired') {
      query = query.or('active.eq.false,expires_at.lt.' + new Date().toISOString());
    }

    const { data, error } = await query;

    if (!error && data) {
      setSessions(data as any);
    }
    setLoading(false);
  };

  const filteredSessions = sessions.filter((session) => {
    if (!search) return true;
    const shopName = session.shops?.name?.toLowerCase() || '';
    const userEmail = session.profiles?.email?.toLowerCase() || '';
    const searchLower = search.toLowerCase();
    return shopName.includes(searchLower) || userEmail.includes(searchLower);
  });

  const formatDuration = (created: string, ended: string | null) => {
    const start = new Date(created);
    const end = ended ? new Date(ended) : new Date();
    const seconds = Math.floor((end.getTime() - start.getTime()) / 1000);
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Support Mode Dashboard</h1>
        <p className="text-muted-foreground">
          View and manage all support sessions
        </p>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by shop name or user..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={filter === 'active' ? 'default' : 'outline'}
            onClick={() => setFilter('active')}
          >
            Active
          </Button>
          <Button
            variant={filter === 'expired' ? 'default' : 'outline'}
            onClick={() => setFilter('expired')}
          >
            Ended
          </Button>
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
          >
            All
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading sessions...</p>
        </div>
      ) : filteredSessions.length === 0 ? (
        <Card className="p-12 text-center">
          <Activity className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="font-semibold mb-2">No sessions found</h3>
          <p className="text-sm text-muted-foreground">
            {filter === 'active' 
              ? 'No active support sessions at the moment'
              : 'No sessions match your filters'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredSessions.map((session) => {
            const isActive = session.active && new Date(session.expires_at) > new Date();
            const timeLeft = isActive 
              ? Math.floor((new Date(session.expires_at).getTime() - Date.now()) / 1000 / 60)
              : 0;

            return (
              <Card key={session.id} className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0 space-y-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4 text-muted-foreground" />
                        <span className="font-semibold">
                          {session.shops?.name || 'Unknown Shop'}
                        </span>
                      </div>
                      <Badge variant={isActive ? 'default' : 'secondary'}>
                        {isActive ? 'Active' : 'Ended'}
                      </Badge>
                      {session.mode === 'USER' && (
                        <Badge variant="outline">
                          <User className="h-3 w-3 mr-1" />
                          User View
                        </Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-muted-foreground">Started by:</span>
                        <p className="font-medium truncate">
                          {session.profiles?.email || 'Unknown'}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Started:</span>
                        <p className="font-medium">
                          {format(new Date(session.created_at), 'PPp')}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Duration:</span>
                        <p className="font-medium">
                          {formatDuration(session.created_at, session.ended_at)}
                        </p>
                      </div>
                      {isActive && (
                        <div>
                          <span className="text-muted-foreground">Time left:</span>
                          <p className={`font-medium ${timeLeft < 10 ? 'text-red-600' : ''}`}>
                            <Clock className="inline h-3 w-3 mr-1" />
                            {timeLeft}m
                          </p>
                        </div>
                      )}
                    </div>

                    {session.note && (
                      <div className="text-sm">
                        <span className="text-muted-foreground">Note:</span>
                        <p className="mt-1">{session.note}</p>
                      </div>
                    )}

                    {session.triggered_from_bug_id && (
                      <Button
                        variant="link"
                        className="px-0 h-auto"
                        onClick={() => navigate(`/bug-reports/${session.triggered_from_bug_id}`)}
                      >
                        View related bug report
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
